import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { KeyIcon, LetterIcon } from "../../../assets/icons";
import CommonDialog from "../../../common/components/dialog/CommonDialog";
import Input from "../../../common/components/new/ui/Input";
import styles from "./LoginDialog.module.scss";
import { hideLoader, showLoader } from "../../../stores/slices/loader-slice";
import CheckoutOneTimePasswordForm from "../../login/components/OneTimePasswordForm/CheckoutOneTimePasswordForm";
import { useLoginMutation } from "../../../service/authentication-service";

interface IProps {
	onCloseDialog: () => void;
}

interface FormType {
	password: string;
	email: string;
}

const LoginDialog = ({ onCloseDialog }: IProps) => {
	const dispatch = useDispatch();
	const [showOTPDialog, setShowOTPDialog] = useState<boolean>(false);
	const [login] = useLoginMutation();
	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<FormType>({
		defaultValues: {
			email: "",
			password: "",
		},
		reValidateMode: "onSubmit",
		mode: "onTouched",
	});

	const registeredInputs = useMemo(() => {
		return {
			password: register("password", { required: "Password is required" }),
			email: register("email", {
				required: "Email is required",
				pattern: {
					value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
					message: "Invalid email address",
				},
			}),
		};
	}, [register, watch]);

	const onSubmit = async (data: FormType) => {
		try {
			dispatch(showLoader());
			await login({ email: data.email, password: data.password }).unwrap();
			setShowOTPDialog(true);
		} catch (error: any) {
			const errorText: any = error.response?.data?.title;
			toast.error(errorText ?? "An unexpected error occurred");
		} finally {
			dispatch(hideLoader());
		}
	};
	return (
		<div>
			{showOTPDialog ? (
				<CommonDialog onCloseDialog={() => {}} noPadding>
					<div className={styles.container}>
						<CheckoutOneTimePasswordForm email={watch("email")} onChangeEmail={() => setShowOTPDialog(false)} />
					</div>
				</CommonDialog>
			) : (
				<CommonDialog onCloseDialog={onCloseDialog} noPadding>
					<div className={styles.container}>
						<div className={styles.banner}>
							<svg width="712" height="316" viewBox="0 0 712 316" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M287.722 292.514L198.001 -134.238C169.608 -123.684 142.578 -110.606 117.138 -95.0045L287.949 292.285L287.722 292.514Z"
									fill="black"
									fillOpacity="0.02"
								/>
								<path d="M504.88 -137.682L414.25 292.513L587.333 -100.054C561.438 -115.197 533.727 -127.816 504.88 -137.682Z" fill="black" fillOpacity="0.02" />
								<path d="M372.443 283.108L463.3 -149.84C433.999 -156.952 403.561 -161.082 372.443 -162V283.108Z" fill="black" fillOpacity="0.02" />
								<path d="M329.526 -161.539C298.407 -159.703 268.197 -154.885 239.123 -147.084L329.526 283.11V-161.539Z" fill="black" fillOpacity="0.02" />
								<path
									d="M185.291 367.541L-73.4248 132.139C-82.5105 155.083 -89.779 179.174 -95.2305 203.723L185.291 367.541Z"
									fill="black"
									fillOpacity="0.02"
								/>
								<path
									d="M164.386 405.169L-102.961 249.151C-105.005 267.277 -106.368 285.632 -106.368 304.446V316.147L164.386 404.939V405.169Z"
									fill="black"
									fillOpacity="0.02"
								/>
								<path
									d="M-11.4297 21.7804C-27.784 43.3475 -42.3211 66.2912 -54.814 90.8409L214.123 335.421L-11.4297 21.7804Z"
									fill="black"
									fillOpacity="0.02"
								/>
								<path
									d="M516.684 367.541L806.517 198.446C800.612 173.208 793.116 148.429 783.349 125.026L516.684 367.771V367.541Z"
									fill="black"
									fillOpacity="0.02"
								/>
								<path
									d="M248.427 309.953L81.023 -70.4538C57.8544 -53.0166 36.503 -33.7439 16.9688 -12.4063L248.654 309.723L248.427 309.953Z"
									fill="black"
									fillOpacity="0.02"
								/>
								<path
									d="M453.555 309.952L689.783 -18.8307C669.568 -40.1683 647.535 -59.2116 623.685 -76.4194L453.555 309.952Z"
									fill="black"
									fillOpacity="0.02"
								/>
								<path d="M487.84 335.189L764.046 83.9556C750.871 59.4058 735.653 36.2327 718.617 14.4362L487.84 335.189Z" fill="black" fillOpacity="0.02" />
								<path
									d="M818.996 312.707V304.447C818.996 283.798 817.178 263.378 814.68 243.417L537.566 404.941L818.996 312.478V312.707Z"
									fill="black"
									fillOpacity="0.02"
								/>
							</svg>
						</div>
						<div className={styles.content}>
							<div className={styles.titleContainer}>
								<div className={styles.title}>Access your account</div>
								<div className={styles.description}>Kindly input your Required Details</div>
							</div>
							<div className={styles.content1}>
								<div className={styles.props}>
									<Input
										ref={registeredInputs.email.ref}
										label="Email Address"
										placeholder="name@sample.com"
										error={errors.email?.message}
										inputProps={registeredInputs.email}
									>
										<LetterIcon />
									</Input>
									<Input
										isPasswordInput
										ref={registeredInputs.password.ref}
										label="Password"
										placeholder="name@sample.com"
										error={errors.password?.message}
										inputProps={registeredInputs.password}
									>
										<KeyIcon />
									</Input>
								</div>
								<div className={styles.controls}>
									<button className={`standardButton ${styles.control} ${styles.back}`} onClick={onCloseDialog}>
										<svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path
												d="M4.79287 9L1.14645 5.35355C0.951184 5.1583 0.951184 4.8417 1.14644 4.64645L4.79287 1"
												stroke="#FF7926"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
										Back
									</button>
									<button onClick={handleSubmit(onSubmit)} className={`standardButton ${styles.control} ${styles.continue}`}>
										Continue
										<svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path
												d="M1.20713 9L4.85355 5.35355C5.04882 5.1583 5.04882 4.8417 4.85356 4.64645L1.20713 1"
												stroke="#FF7926"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</button>
								</div>
							</div>
						</div>
					</div>
				</CommonDialog>
			)}
		</div>
	);
};
export default LoginDialog;
